import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import CIALabsBadge from "./components/CIALabsBadge";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";

// Auth Pages
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminSilentAccess from "./pages/SuperAdminSilentAccess";
import SystemMonitor from "./pages/SystemMonitor";
import FacturamaConfig from "./pages/FacturamaConfig";
import StripeConfig from "./pages/StripeConfig";
import TicketsAdmin from "./pages/TicketsAdmin";
import CompanyLogin from "./pages/CompanyLogin";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";

// Company Pages
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import CRM from "./pages/CRM";
import Quotes from "./pages/Quotes";
import Invoices from "./pages/Invoices";
import Purchases from "./pages/Purchases";
import Suppliers from "./pages/Suppliers";
import Documents from "./pages/Documents";
import FieldReports from "./pages/FieldReports";
import KPIs from "./pages/KPIs";
import Intelligence from "./pages/Intelligence";
import Settings from "./pages/Settings";
import Tickets from "./pages/Tickets";
import UserProfile from "./pages/UserProfile";
import Reminders from "./pages/Reminders";
import DocumentSettings from "./pages/DocumentSettings";
import ActivityLogs from "./pages/ActivityLogs";
import FiscalSettings from "./pages/FiscalSettings";
import SubscriptionBilling from "./pages/SubscriptionBilling";
import MySubscription from "./pages/MySubscription";
import PendingReceipts from "./pages/PendingReceipts";
import SetupWizard from "./pages/SetupWizard";
import PublicSignup from "./pages/PublicSignup";
import SubscriptionExpired from "./pages/SubscriptionExpired";
import LandingPage from "./pages/LandingPage";
import FindCompany from "./pages/FindCompany";

// Layout
import MainLayout from "./components/Layout/MainLayout";

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-muted-foreground">Cargando...</p>
    </div>
  </div>
);

// Super Admin Protected Route
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (!user || user.role !== "super_admin") {
    return <Navigate to="/admin-portal" replace />;
  }

  return children;
};

// Company Protected Route
const CompanyRoute = ({ children }) => {
  const { user, loading, companySlug, company } = useAuth();
  const { slug } = useParams();

  if (loading) return <LoadingScreen />;

  if (!user) {
    return <Navigate to={`/empresa/${slug}/login`} replace />;
  }

  if (user.role === "super_admin") {
    return <Navigate to="/admin-portal/dashboard" replace />;
  }

  if (companySlug && companySlug !== slug) {
    return <Navigate to={`/empresa/${companySlug}/login`} replace />;
  }

  // Check if subscription has expired (trial ended without payment)
  if (company) {
    const subscriptionStatus = company.subscription_status;
    const currentPath = window.location.pathname;
    const isSubscriptionPath = currentPath.includes("/subscription");
    const isExpiredPath = currentPath.includes("/expired");
    
    // If subscription expired, redirect to expired page (unless already there or on subscription page)
    if (subscriptionStatus === "expired" && !isSubscriptionPath && !isExpiredPath) {
      return <Navigate to={`/empresa/${slug}/expired`} replace />;
    }
    
    // Check if trial has ended
    if (subscriptionStatus === "trial" && company.trial_ends_at) {
      const trialEnd = new Date(company.trial_ends_at);
      if (new Date() > trialEnd && !isSubscriptionPath && !isExpiredPath) {
        return <Navigate to={`/empresa/${slug}/expired`} replace />;
      }
    }
    
    // Check if setup is incomplete and redirect to wizard
    // Only for admin users with active/trial subscription
    if (user.role === "admin" && (subscriptionStatus === "active" || subscriptionStatus === "trial")) {
      const needsSetup = !company.business_name || 
                         !company.rfc || 
                         !company.regimen_fiscal;
      
      const isSetupPath = currentPath.includes("/setup");
      
      if (needsSetup && !isSetupPath && !isSubscriptionPath) {
        return <Navigate to={`/empresa/${slug}/setup`} replace />;
      }
    }
  }

  return children;
};

// Public Route for Super Admin
const PublicSuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (user && user.role === "super_admin") {
    return <Navigate to="/admin-portal/dashboard" replace />;
  }

  return children;
};

// Public Route for Company
const PublicCompanyRoute = ({ children }) => {
  const { user, loading, companySlug } = useAuth();
  const { slug } = useParams();

  if (loading) return <LoadingScreen />;

  if (user && user.role !== "super_admin" && companySlug === slug) {
    return <Navigate to={`/empresa/${slug}/dashboard`} replace />;
  }

  return children;
};

// Home Page - Show Landing Page for unauthenticated users, redirect authenticated users
const HomePage = () => {
  const { user, loading, companySlug, isSuperAdmin } = useAuth();

  if (loading) return <LoadingScreen />;

  // If user is authenticated, redirect to appropriate dashboard
  if (user) {
    if (isSuperAdmin()) {
      return <Navigate to="/admin-portal/dashboard" replace />;
    }
    if (companySlug) {
      return <Navigate to={`/empresa/${companySlug}/dashboard`} replace />;
    }
  }

  // Show Landing Page for unauthenticated users
  return <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Home */}
      <Route path="/" element={<HomePage />} />

      {/* Super Admin Portal */}
      <Route
        path="/admin-portal"
        element={
          <PublicSuperAdminRoute>
            <SuperAdminLogin />
          </PublicSuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/dashboard"
        element={
          <SuperAdminRoute>
            <SuperAdminDashboard />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/system-monitor"
        element={
          <SuperAdminRoute>
            <SystemMonitor />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/facturama"
        element={
          <SuperAdminRoute>
            <FacturamaConfig />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/silent-access"
        element={
          <SuperAdminRoute>
            <SuperAdminSilentAccess />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/stripe"
        element={
          <SuperAdminRoute>
            <StripeConfig />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/tickets"
        element={
          <SuperAdminRoute>
            <TicketsAdmin />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/subscriptions"
        element={
          <SuperAdminRoute>
            <SubscriptionBilling />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/activity-logs"
        element={
          <SuperAdminRoute>
            <ActivityLogs isSuperAdmin={true} />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin-portal/pending-receipts"
        element={
          <SuperAdminRoute>
            <PendingReceipts />
          </SuperAdminRoute>
        }
      />

      {/* Password Reset Routes */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/empresa/:slug/forgot-password" element={<ForgotPassword />} />

      {/* Public Signup Route */}
      <Route path="/registro" element={<PublicSignup />} />
      <Route path="/signup" element={<PublicSignup />} />
      <Route path="/login" element={<FindCompany />} />
      <Route path="/iniciar-sesion" element={<FindCompany />} />
      <Route path="/empresa/:slug/reset-password/:token" element={<ResetPassword />} />

      {/* Company Login */}
      <Route
        path="/empresa/:slug/login"
        element={
          <PublicCompanyRoute>
            <CompanyLogin />
          </PublicCompanyRoute>
        }
      />

      {/* Company App Routes */}
      <Route
        path="/empresa/:slug"
        element={
          <CompanyRoute>
            <MainLayout />
          </CompanyRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="crm" element={<CRM />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="documents" element={<Documents />} />
        <Route path="field-reports" element={<FieldReports />} />
        <Route path="kpis" element={<KPIs />} />
        <Route path="intelligence" element={<Intelligence />} />
        <Route path="settings" element={<Settings />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="reminders" element={<Reminders />} />
        <Route path="document-settings" element={<DocumentSettings />} />
        <Route path="activity-logs" element={<ActivityLogs />} />
        <Route path="fiscal-settings" element={<FiscalSettings />} />
        <Route path="subscription" element={<MySubscription />} />
        <Route path="setup" element={<SetupWizard />} />
        <Route path="expired" element={<SubscriptionExpired />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            expand={true}
            duration={4000}
          />
          <CIALabsBadge />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
